import type { Vec2 } from "../types";

/** Minimum samples required per phase for a calibration run to be usable. */
const MIN_SAMPLES_PER_PHASE = 5;
/** Minimum |median delta| (raw gaze units) between opposing phases to trust the sign/gain. */
const MIN_DELTA = 0.04;
/** Floor on the half-range used to derive gain, so a near-zero delta can't blow up to Infinity. */
const GAIN_HALF_RANGE_FLOOR = 0.05;

export interface GazeCalibration {
  /** Raw gaze reading (screen-centre look) subtracted before sign/gain are applied. */
  neutral: Vec2;
  signX: 1 | -1;
  signY: 1 | -1;
  gainX: number;
  gainY: number;
  /** False when the calibration run didn't produce a trustworthy sign/gain (too few
   * samples, or too small a measured glance range) — callers should fall back to
   * raw/uncalibrated gaze, not steer off an unreliable fit. */
  ok: boolean;
}

export const DEFAULT_CALIBRATION: GazeCalibration = {
  neutral: { x: 0, y: 0 },
  signX: 1,
  signY: 1,
  gainX: 1,
  gainY: 1,
  ok: false,
};

export interface CalibrationSamplePhases {
  neutral: Vec2[];
  up: Vec2[];
  down: Vec2[];
  left: Vec2[];
  right: Vec2[];
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
}

function medianVec2(samples: Vec2[]): Vec2 {
  return { x: median(samples.map((s) => s.x)), y: median(samples.map((s) => s.y)) };
}

function gainFromDelta(delta: number): number {
  return 1 / Math.max(GAIN_HALF_RANGE_FLOOR, Math.abs(delta) / 2);
}

/**
 * Derives a {@link GazeCalibration} from five sampled gaze phases (screen centre, and a
 * held glance in each cardinal direction): per-phase medians (robust to blink/saccade
 * outliers mid-phase), then dy/dx between opposing phases fix each axis' sign and gain
 * so a measured half-range glance maps to `applyCalibration` output of roughly ±1.
 * Falls back to {@link DEFAULT_CALIBRATION} (keeping a real `neutral` when there were
 * enough neutral samples to measure one) when any phase is under-sampled or the
 * measured range is too small to trust.
 */
export function calibrationFromSamples(s: CalibrationSamplePhases): GazeCalibration {
  const enoughSamples =
    s.neutral.length >= MIN_SAMPLES_PER_PHASE &&
    s.up.length >= MIN_SAMPLES_PER_PHASE &&
    s.down.length >= MIN_SAMPLES_PER_PHASE &&
    s.left.length >= MIN_SAMPLES_PER_PHASE &&
    s.right.length >= MIN_SAMPLES_PER_PHASE;

  const neutral =
    s.neutral.length >= MIN_SAMPLES_PER_PHASE ? medianVec2(s.neutral) : DEFAULT_CALIBRATION.neutral;

  if (!enoughSamples) {
    return { ...DEFAULT_CALIBRATION, neutral };
  }

  const dy = medianVec2(s.down).y - medianVec2(s.up).y;
  const dx = medianVec2(s.right).x - medianVec2(s.left).x;
  const ok = Math.abs(dx) >= MIN_DELTA && Math.abs(dy) >= MIN_DELTA;

  if (!ok) {
    return { ...DEFAULT_CALIBRATION, neutral };
  }

  return {
    neutral,
    signX: dx >= 0 ? 1 : -1,
    signY: dy >= 0 ? 1 : -1,
    gainX: gainFromDelta(dx),
    gainY: gainFromDelta(dy),
    ok: true,
  };
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Maps a raw gaze reading through a calibration: subtracts `neutral`, applies the
 * measured per-axis sign and gain, an optional extra `gain` multiplier, and optional
 * per-axis inversion — then clamps each axis to `[-1, 1]`.
 */
export function applyCalibration(
  raw: Vec2,
  cal: GazeCalibration,
  opts: { gain?: number; invertX?: boolean; invertY?: boolean } = {},
): Vec2 {
  const gain = opts.gain ?? 1;
  const invertX = opts.invertX ? -1 : 1;
  const invertY = opts.invertY ? -1 : 1;

  const x = (raw.x - cal.neutral.x) * cal.signX * cal.gainX * gain * invertX;
  const y = (raw.y - cal.neutral.y) * cal.signY * cal.gainY * gain * invertY;

  return { x: clamp(x, -1, 1), y: clamp(y, -1, 1) };
}
